import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const GroupsSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('groups', 'edit');
  const canAdd = can('groups', 'add');
  const canDelete = can('groups', 'delete');

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', role_ids: [] });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [groupsRes, rolesRes] = await Promise.all([
        api.get('/access/groups'),
        api.get('/access/roles')
      ]);
      setGroups(groupsRes.data?.data || []);
      setRoles(rolesRes.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingGroup(null);
    setForm({ name: '', description: '', role_ids: [] });
    setDialogOpen(true);
  };

  const openEdit = (group) => {
    setEditingGroup(group);
    setForm({
      name: group.name,
      description: group.description || '',
      role_ids: (group.roles || []).map((role) => role.id)
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingGroup) {
        await api.put(`/access/groups/${editingGroup.id}`, form);
        toast.success('Group updated');
      } else {
        await api.post('/access/groups', form);
        toast.success('Group created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save group');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/access/groups/${deleteTarget.id}`);
      toast.success('Group deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete group');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Groups</Typography>
          <Typography variant="body2" color="text.secondary">
            Create groups and assign roles. Users inherit all permissions from roles linked to their groups.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          {canAdd && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Group
            </Button>
          )}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Flow: <strong>User → Group → Role(s) → Permissions</strong>. Assign users to groups in the Users module.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell sx={{ fontWeight: 600 }}>{group.name}</TableCell>
                <TableCell>{group.description || '—'}</TableCell>
                <TableCell>
                  {(group.roles || []).map((role) => (
                    <Chip key={role.id} label={role.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>
                  {group.is_system && <Chip size="small" label="System" color="primary" />}
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(group)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && !group.is_system && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(group)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? `Edit Group: ${editingGroup.name}` : 'Create Group'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Group Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
              disabled={editingGroup?.is_system}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={form.role_ids}
                onChange={(e) => setForm({ ...form, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')
                }
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name} ({role.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.role_ids.length}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          Delete group &quot;{deleteTarget?.name}&quot;? Users in this group will lose associated role permissions.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupsSettings;
